import type { BasePagingResponse } from "../../../lib/api-contract";
import { apiUtils } from "../../../utils/apiUtils";
import type {
  NewsDetailResponse,
  NewsListItemResponse,
  NewsListRequest,
  NewsListResponse,
  NewsStatistics,
  NewsStatisticsRequest,
  NewsSubmitRequest,
} from "../types/news.types";

const toNewsListParams = (request: NewsListRequest) => ({
  index: request.pagination.index,
  size: request.pagination.size,
  keyword: request.keyword?.trim() || undefined,
  sort_key: request.sorting?.sort_key,
  sort_direction: request.sorting?.sort_direction,
});

const buildNewsFormData = (request: NewsSubmitRequest): FormData => {
  const formData = new FormData();
  formData.append("title", request.title);
  formData.append("content", request.content);

  if (request.location) {
    formData.append("location", request.location);
  }

  if (request.thumbnail_url) {
    formData.append("thumbnail_url", request.thumbnail_url);
  }

  if (request.thumbnail_file) {
    formData.append("thumbnail_file", request.thumbnail_file);
  }

  return formData;
};

export async function getNewsList(
  request: NewsListRequest,
  signal?: AbortSignal,
): Promise<NewsListResponse> {
  const data = await apiUtils.getData<BasePagingResponse<NewsListItemResponse>>(
    "/v1/portal/new",
    "Failed to fetch news",
    { params: toNewsListParams(request), signal },
  );

  return {
    paging: {
      index: data.paging?.index ?? request.pagination.index,
      size: data.paging?.size ?? request.pagination.size,
      count: data.paging?.count ?? 0,
      total: data.paging?.total ?? 0,
    },
    list: [...(data.list ?? [])],
  };
}

export function getNewsDetail(id: string, signal?: AbortSignal) {
  return apiUtils.getData<NewsDetailResponse>(
    `/v1/portal/new/${id}`,
    "News article not found",
    { signal },
  );
}

export async function getNewsStatistics(
  request: NewsStatisticsRequest = {},
  signal?: AbortSignal,
): Promise<NewsStatistics> {
  return apiUtils.getData<NewsStatistics>(
    "/v1/portal/new/statistics",
    "Failed to fetch news statistics",
    {
      params: {
        from: request.from || undefined,
        to: request.to || undefined,
      },
      signal,
    },
  );
}

export function createNews(request: NewsSubmitRequest) {
  return apiUtils.postWithFormData<NewsDetailResponse>("/v1/portal/new", buildNewsFormData(request));
}

export function updateNews(id: string, request: NewsSubmitRequest) {
  return apiUtils.putWithFormData<NewsDetailResponse>(
    `/v1/portal/new/${id}`,
    buildNewsFormData(request),
  );
}
